import type { RegionAliases } from "@entity/Region";
import type { ServiceReport } from "@entity/ServiceReport";
import type { EventsBlockReport, ReportListData } from "@entity/ServiceRow";
import { getServiceNameSubname } from "./serviceName";

/**
 * Collects displayed region headings in first-seen order, including Other rows.
 * Unaliased names keep their original casing, matching the legacy events-block fallback.
 */
export function extractRegionNames(report: ServiceReport, aliases: RegionAliases): string[] {
  const regions = new Set<string>();
  report.forEach((item) => {
    const rawRegion = item.region;
    const key = rawRegion.toLowerCase();
    const normalizedRegion = Object.prototype.hasOwnProperty.call(aliases, key)
      ? aliases[key]
      : rawRegion;

    regions.add(normalizedRegion);
  });

  return Array.from(regions);
}

/**
 * Groups report indices by service, subservice and displayed region, excluding Other.
 * Does not write region headings; callers keep those in store state.
 */
export function deriveEventsBlock(
  report: ServiceReport,
  regionAliases: RegionAliases,
  regionNames: string[],
): EventsBlockReport {
  if (report.length === 0) {
    return [];
  }

  const data: Record<string, ReportListData> = Object.create(null);
  report.forEach((item, index) => {
    if (item.service === "Other") {
      return;
    }

    const { name, subName } = getServiceNameSubname(item);
    const regionKey = item.region.toLowerCase();
    const currentRegion = Object.prototype.hasOwnProperty.call(regionAliases, regionKey)
      ? regionAliases[regionKey]
      : item.region;
    const regionIndex = regionNames.findIndex((region) => region === currentRegion);
    const serviceData: ReportListData = data[name] ??= Object.create(null);
    const subServiceData = serviceData[subName] ??= { ids: [] };

    subServiceData.ids[regionIndex] = index;
  });

  return data;
}